/**
 * Data Synchronization Module
 * Handles synchronization between source and target tables
 */

/**
 * Match Only Sync: Update target rows where grouper values match
 * @param sourceData - Source table as 2D array
 * @param targetData - Target table as 2D array
 * @param grouperColumns - Column names that define row keys (e.g., ["Group"])
 * @returns Updated target table
 */
export function matchOnlySync(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[]
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Find grouper column indices
  const sourceGrouperIndices = findGrouperIndices(sourceHeaders, grouperColumns);
  const targetGrouperIndices = findGrouperIndices(targetHeaders, grouperColumns);
  
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    logWarning('No grouper columns found for matching', "data-sync", undefined);
    return targetData;
  }
  
  // Build source row map by grouper key
  const sourceRowMap = new Map<string, any[]>();
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKey(sourceRow, sourceGrouperIndices);
    sourceRowMap.set(rowKey, sourceRow);
  });
  
  // Build column mapping (source col index -> target col index)
  const columnMapping = buildColumnMapping(sourceHeaders, targetHeaders);
  
  // Update target rows where there's a match
  const updatedTargetRows = targetRows.map((targetRow: any[]) => {
    const targetRowKey = buildRowKey(targetRow, targetGrouperIndices);
    const sourceRow = sourceRowMap.get(targetRowKey);
    
    if (sourceRow) {
      // Match found - update target row with source values
      const updatedRow = [...targetRow];
      columnMapping.forEach((targetIndex, sourceIndex) => {
        updatedRow[targetIndex] = sourceRow[sourceIndex];
      });
      return updatedRow;
    }
    
    // No match - keep target row unchanged
    return targetRow;
  });
  
  // Return updated target table
  return [targetHeaders, ...updatedTargetRows];
}

/**
 * Match All Sync: Replace entire target table with source data (matching columns)
 * @param sourceData - Source table as 2D array
 * @param targetData - Target table as 2D array
 * @returns Updated target table
 */
export function matchAllSync(
  sourceData: any[][],
  targetData: any[][]
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  
  // Build column mapping (source col index -> target col index)
  const columnMapping = buildColumnMapping(sourceHeaders, targetHeaders);
  
  // Create new target rows with source data
  const newTargetRows = sourceRows.map((sourceRow: any[]) => {
    const newRow = new Array(targetHeaders.length).fill('');
    
    // Copy matched columns from source to target
    columnMapping.forEach((targetIndex, sourceIndex) => {
      newRow[targetIndex] = sourceRow[sourceIndex];
    });
    
    return newRow;
  });
  
  // Return new target table with source rows
  return [targetHeaders, ...newTargetRows];
}

/**
 * Get sync summary message
 */
export function getSyncSummary(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[]
): string {
  if (!sourceData || sourceData.length <= 1 || !targetData || targetData.length <= 1) {
    return 'No data to sync';
  }
  
  const sourceRows = sourceData.length - 1; // Exclude header
  const targetRows = targetData.length - 1; // Exclude header
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  
  // Find grouper column indices
  const sourceGrouperIndices = findGrouperIndices(sourceHeaders, grouperColumns);
  const targetGrouperIndices = findGrouperIndices(targetHeaders, grouperColumns);
  
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    return `Source: ${sourceRows} rows\nTarget: ${targetRows} rows\nNo grouper columns for matching`;
  }
  
  // Build source row keys
  const sourceRowKeys = new Set<string>();
  sourceData.slice(1).forEach((row: any[]) => {
    sourceRowKeys.add(buildRowKey(row, sourceGrouperIndices));
  });
  
  // Count matches in target
  let matchedRows = 0;
  let unmatchedRows = 0;
  
  targetData.slice(1).forEach((row: any[]) => {
    const rowKey = buildRowKey(row, targetGrouperIndices);
    if (sourceRowKeys.has(rowKey)) {
      matchedRows++;
    } else {
      unmatchedRows++;
    }
  });
  
  // Count new rows in source
  const targetRowKeys = new Set<string>();
  targetData.slice(1).forEach((row: any[]) => {
    targetRowKeys.add(buildRowKey(row, targetGrouperIndices));
  });
  
  let newRows = 0;
  sourceData.slice(1).forEach((row: any[]) => {
    const rowKey = buildRowKey(row, sourceGrouperIndices);
    if (!targetRowKeys.has(rowKey)) {
      newRows++;
    }
  });
  
  return [
    `Source: ${sourceRows} rows`,
    `Target: ${targetRows} rows`,
    `Match Only: ${matchedRows} rows will be updated`,
    `New rows in source: ${newRows}`,
    `Unmatched in target: ${unmatchedRows}`
  ].join('\n');
}

/**
 * Find grouper column indices by column labels
 */
function findGrouperIndices(headers: any[], grouperColumns: string[]): number[] {
  const indices: number[] = [];
  
  grouperColumns.forEach(grouperLabel => {
    const normalizedLabel = grouperLabel.toLowerCase().trim();
    const index = headers.findIndex((h: any) => 
      h?.toString().toLowerCase().trim() === normalizedLabel
    );
    if (index !== -1) {
      indices.push(index);
    }
  });
  
  return indices;
}

/**
 * Build column mapping between source and target headers
 * @returns Map of source column index to target column index
 */
function buildColumnMapping(sourceHeaders: any[], targetHeaders: any[]): Map<number, number> {
  const mapping = new Map<number, number>();
  
  sourceHeaders.forEach((sourceHeader: any, sourceIndex: number) => {
    const normalizedSourceHeader = sourceHeader?.toString().toLowerCase().trim();
    
    // First try to match by exact header name
    let targetIndex = targetHeaders.findIndex((targetHeader: any) => 
      targetHeader?.toString().toLowerCase().trim() === normalizedSourceHeader
    );
    
    // If no exact match, try matching by removing special characters and spaces
    if (targetIndex === -1) {
      const simplifiedSource = normalizedSourceHeader.replace(/[^a-z0-9]/g, '');
      targetIndex = targetHeaders.findIndex((targetHeader: any) => {
        const simplifiedTarget = targetHeader?.toString().toLowerCase().trim().replace(/[^a-z0-9]/g, '');
        return simplifiedTarget === simplifiedSource;
      });
    }
    
    if (targetIndex !== -1) {
      mapping.set(sourceIndex, targetIndex);
    }
  });
  
  return mapping;
}

/**
 * Build a row key from grouper column values
 */
function buildRowKey(row: any[], grouperIndices: number[]): string {
  return grouperIndices
    .map(index => row[index]?.toString().toLowerCase().trim() || '')
    .join('|');
}

